import { Intent } from '@blueprintjs/core';
import * as React from 'react';
import { Text } from 'vet-bones/components';
import { alphaSort } from 'vet-bones/utils';
import { QueryBuilderModel } from 'vet-bones/utils/queryBuilder';

import { withConfirmation } from 'src/components/withConfirmation';
import { AttributesToRemoveInfo } from 'src/containers/ProjectDiagram/hooks/remove/types';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { getQueryBuilderModel } from 'src/containers/ProjectDiagram/utils/getQueryBuilderModel';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { isRuleValid } from 'src/containers/ProjectDiagram/utils/rules/isRuleValid';
import * as copy from 'src/templates/copy';

const getInvalidRules = (
  rulesById: Record<string, ModelRule>,
  ruleModel: QueryBuilderModel
) => Object.values(rulesById).filter((rule) => !isRuleValid(rule, ruleModel));

export const checkAndConfirmCommit = (
  diagram: Diagram,
  commit: (model: Model, diagram: Diagram) => void,
  tname: string | undefined,
  attributesToRemoveInfo?: AttributesToRemoveInfo[]
) => {
  return new Promise<boolean>((resolve) => {
    diagram.model.startTransaction(tname);
    const ruleModel = getQueryBuilderModel(diagram.model);
    const oldInvalidRuleIdsSet: Set<string> = ruleModel
      ? new Set(
          getInvalidRules(diagram.model.modelData.rulesById, ruleModel).map(
            (rule) => rule.id
          )
        )
      : new Set();

    commit(diagram.model, diagram);

    const updatedRuleModel = getQueryBuilderModel(diagram.model);
    const invalidRulesAfterCommit = ruleModel
      ? getInvalidRules(diagram.model.modelData.rulesById, updatedRuleModel)
      : [];
    const newlyInvalidRules = invalidRulesAfterCommit.filter(
      (rule) => !oldInvalidRuleIdsSet.has(rule.id)
    );

    // Check for no invalid rules and no attributes to remove if attributesToRemoveInfo exists
    if (
      !newlyInvalidRules.length &&
      (attributesToRemoveInfo
        ? attributesToRemoveInfo?.every(
            (info) => !info.attributesToRemove.length
          )
        : true)
    ) {
      diagram.model.commitTransaction(tname);
      resolve(true);
      return;
    }

    const { labelsByIri } = (diagram.model as Model).modelData;
    const message = [];

    attributesToRemoveInfo?.forEach((info) => {
      if (info.attributesToRemove.length) {
        message.push(
          <div key={info.attributesToRemove[0].value}>
            <Text>
              {copy.components.projectDiagram.modificationWarning.attributesMessage(
                getLabelForIri(labelsByIri, info.classIri)
              )}
            </Text>
            <ul>
              {info?.attributesToRemove.map((attribute) => (
                <li key={attribute.value}>
                  {getLabelForIri(labelsByIri, attribute.value)}
                </li>
              ))}
            </ul>
          </div>
        );
      }
    });

    if (newlyInvalidRules.length) {
      message.push(
        <div key={newlyInvalidRules[0].id}>
          <Text>
            {copy.components.projectDiagram.modificationWarning.rulesMessage}
          </Text>
          <ul>
            {newlyInvalidRules
              .sort(alphaSort((rule) => rule.name))
              .map((rule) => (
                <li key={rule.id}>{rule.name}</li>
              ))}
          </ul>
        </div>
      );
    }

    withConfirmation({
      dialogClassName: 'sd-dialog-remove-class-warning',
      isCloseButtonShown: false,
      message,
      noButtonOutlined: true,
      noPrompt: copy.components.projectDiagram.modificationWarning.cancel,
      title: null,
      yesButtonIntent: Intent.PRIMARY,
      yesPrompt: copy.components.projectDiagram.modificationWarning.proceed,
      onConfirmationSuccess: () => {
        diagram.model.commitTransaction(tname);
        resolve(true);
      },
      onConfirmationFailure: () => {
        diagram.model.rollbackTransaction();
        resolve(false);
      },
    });
  });
};
